import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Manages table pagination + sort state and syncs it with the URL query
// string. Preserves any OTHER query params (e.g. ?tab=games) so it can be
// composed with tabbed pages — the page can own its own URL keys and this
// hook won't trample them.
export const useTableUrlState = ({ initialSorting = [], initialPageSize = 10}) => {
    const location = useLocation();
    const navigate = useNavigate();

    // Parse once on mount — used only to seed initial state. After that,
    // the URL is driven by the effect below.
    const initialParams = useMemo(() => new URLSearchParams(location.search), []); // eslint-disable-line react-hooks/exhaustive-deps

    const [pagination, setPagination] = useState(() => {
        const page = parseInt(initialParams.get('page'), 10);
        const size = parseInt(initialParams.get('size'), 10);
        return {
            pageIndex: !isNaN(page) && page > 0 ? page - 1 : 0,
            pageSize: !isNaN(size) && size > 0 ? size : initialPageSize,
        };
    });

    const [sorting, setSorting] = useState(() => {
        const sort = initialParams.get('sort');
        if (sort) {
            const [id, desc] = sort.split(',');
            return [{ id, desc: desc === 'desc' }];
        }
        return initialSorting;
    });

    // Sync pagination/sort → URL on every change. We start from the
    // CURRENT URL params (not an empty set) so unrelated keys like ?tab=
    // are preserved. Identity guard at the end skips no-op navigations,
    // which prevents an effect loop when location.search itself is a dep.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        params.set('page', pagination.pageIndex + 1);
        params.set('size', pagination.pageSize);
        if (sorting.length > 0) {
            params.set('sort', `${sorting[0].id},${sorting[0].desc ? 'desc' : 'asc'}`);
        } else {
            params.delete('sort');
        }
        const next = params.toString();
        const current = location.search.startsWith('?') ? location.search.slice(1) : location.search;
        if (next === current) return;
        navigate(`${location.pathname}?${next}`, { replace: true });
    }, [pagination, sorting, navigate, location.pathname, location.search]);

    return {
        pagination,
        setPagination,
        sorting,
        setSorting,
    };
};
